
@value focusControl: :is(input:not([type="hidden"], [aria-hidden="true"]), textarea, select, [data-slot="select-trigger"]);
@value singleControlGroup: has(> focusControl):not(:has(> focusControl ~ focusControl));

.root {
	position: relative;
	display: flex;
	align-items: stretch;
	width: 100%;
}

/* `.input-group[class*="col-"]` — a group that IS a grid column must not float or pad. */
.root[class*="col-"] {
	float: none;
	padding-left: 0;
	padding-right: 0;
}


.root > *:not(.addon):not(.button):not([data-slot="button"]) {
	position: relative;
	z-index: 2;
	flex: 1 1 auto;
	width: auto;
	min-width: 0;
	margin-bottom: 0;
}

.root > *:not(.addon):not(.button):not([data-slot="button"]):focus {
	z-index: 3;
}

/* A single control shares one ring with its addons and buttons. */
.root:singleControlGroup:focus-within {
	box-shadow: 0 0 0 3px var(--color-primary-focus-ring);
	border-radius: var(--input-border-radius);
}

.root:singleControlGroup > focusControl:focus,
.root:singleControlGroup :is(.button, [data-slot="button"]):focus-visible {
	box-shadow: none;
}

/* Collapse the seam to a single 1px line. */
.root > * + * {
	margin-left: -1px;
}


.root > *:not(:first-child) {
	border-top-left-radius: 0;
	border-bottom-left-radius: 0;
}

.root > *:not(:last-child) {
	border-top-right-radius: 0;
	border-bottom-right-radius: 0;
}

/* A hidden input is not a cell, so it must not take `:first-child` away from the one that
   follows it — that cell would lose its left radius and be pulled 1px outside the group. The
   app's most repeated interactive shape starts with one (36 blocks; see
   shared/ui/primitives/ComboboxInput.tsx). */
.root > input[type="hidden"]:first-child + * {
	margin-left: 0;
	border-top-left-radius: var(--input-border-radius);
	border-bottom-left-radius: var(--input-border-radius);
}

/* The trailing counterpart: Base UI's Select and Combobox roots render a visually hidden
   <input> *after* their children for form integration. It takes `:last-child` from the control
   in front of it, which then keeps its right corners squared off although it ends the group.
   Only visible where nothing follows the control — the hour picker in the e-mail settings has a
   trailing addon in German but not in English. */
.root > *:has(+ input[aria-hidden="true"]:last-child) {
	border-top-right-radius: var(--input-border-radius);
	border-bottom-right-radius: var(--input-border-radius);
}


.addon {
	flex: none;
	display: flex;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	/* a cell rendered as <label> would otherwise keep the theme's 7px label margin, which
	   under `align-items: stretch` makes it that much shorter than the rest of the group */
	margin-bottom: 0;
	padding: var(--padding-base-y) var(--padding-base-x);
	font-size: var(--font-size-base);
	font-weight: normal;
	line-height: 1;
	color: var(--input-group-addon-color);
	white-space: nowrap;
	background-color: var(--input-group-addon-bg);
	border: 1px solid var(--input-group-addon-border);
	border-radius: var(--input-border-radius);
}

/*
 * A square, unpadded cell — what the theme gives every leading addon via
 * `.input-group-addon:first-child { min-width: 36px; padding: 0 }`. Here it is opt-in instead
 * of positional, so a text addon does not silently lose its padding for being first.
 */
.addon.square {
	min-width: var(--input-height);
	padding: 0;
}


.root .button,
.root > [data-slot="button"] {
	position: relative;
	z-index: 2;
	flex: none;
	min-width: var(--input-height);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	/* same reason as on .addon — a button rendered as <label> (the date-picker shape) */
	margin-bottom: 0;
}


.root .button {
	padding: 0 9px;
}

.root .button:disabled,
.root .button[data-disabled],
.root > [data-slot="button"]:disabled,
.root > [data-slot="button"][data-disabled] {
	opacity: 1;
	cursor: var(--cursor-disabled);
}

.root .button:disabled > *,
.root .button[data-disabled] > *,
.root > [data-slot="button"]:disabled > *,
.root > [data-slot="button"][data-disabled] > * {
	opacity: 0.65;
}


.root[data-invalid] > *:not(.addon):not(.button):not([data-slot="button"]) {
	border-color: var(--state-danger-text);
}

.root[data-invalid] .addon,
.root:has([data-invalid]) .addon,
.root[data-invalid] .button,
.root[data-invalid] > [data-slot="button"],
.root:has([data-invalid]) .button,
.root:has([data-invalid]) > [data-slot="button"] {
	color: var(--state-danger-text);
	background-color: var(--state-danger-bg);
	border-color: var(--state-danger-text);
}
